import { useMemo } from "react";
import { MenuItem } from "@/components/LeftMenu/types";
import { PermissionKey } from "@/types/permissions";
import { usePermissions } from "@/providers/permissions-provider";

/**
 * Permission mapping for menu items
 * Maps menu item IDs to the permissions required to view them
 */
const MENU_PERMISSIONS: Record<string, PermissionKey | PermissionKey[]> = {
  // Top Level Pages
  "dashboard": "dashboard.view",
  "goods-in-out": ["goods-in-out.view"],
  "stock-assistant": "stock-assistant.view",
  "quick-start": "quick-start.view",
  
  // Inventory Section
  "inventory": ["inventory.stock-items.view", "inventory.non-stock-items.view", "inventory.stock-transfers.view", "inventory.movements.view", "inventory.tracking.view"],
  "stock-items": "inventory.stock-items.view",
  "non-stock-items": "inventory.non-stock-items.view",
  "stock-transfers": "inventory.stock-transfers.view",
  "stock-movements": "inventory.movements.view",
  "tracking": "inventory.tracking.view",
  
  // Warehouse Operations
  "warehouse-operations": ["warehouse.receive-goods.view", "warehouse.locations.view", "warehouse.stock-takes.view"],
  "receive-goods": "warehouse.receive-goods.view",
  "warehouses": "warehouse.locations.view",
  "stock-takes": "warehouse.stock-takes.view",
  
  // Purchasing
  "purchasing": ["purchasing.orders.view", "purchasing.suppliers.view"],
  "purchase-orders": "purchasing.orders.view",
  "suppliers": "purchasing.suppliers.view",
  
  // Sales
  "sales": ["sales.orders.view", "sales.customers.view"],
  "sales-orders": "sales.orders.view",
  "customers": "sales.customers.view",
  
  // Manufacturing
  "manufacturing": ["manufacturing.boms.view", "manufacturing.work-orders.view"],
  "boms": "manufacturing.boms.view",
  "work-orders": "manufacturing.work-orders.view",
  
  // Projects & Cost Centers
  "projects-cost-centers": "projects.view",
  
  // Analytics & Forecasting
  "analytics": ["analytics.forecasting.view", "analytics.abc-classification.view", "analytics.reorder.view", "analytics.margin-analysis.view", "analytics.cost-analysis.view"],
  "ai-forecasting": "analytics.forecasting.view",
  "abc-classification": "analytics.abc-classification.view",
  "reorder": "analytics.reorder.view",
  "margin-analysis": "analytics.margin-analysis.view",
  "cost-analysis": "analytics.cost-analysis.view",
  
  // Pricing
  "pricing": ["pricing.matrix.view", "pricing.advanced.view"],
  "price-matrix": "pricing.matrix.view",
  "advanced-pricing": "pricing.advanced.view",
  
  // Settings
  "settings": ["settings.system-config.view", "settings.location-settings.view", "settings.integration.view", "settings.company.view", "settings.user-management.view", "settings.custom-fields.view"],
  "stock-options": "settings.system-config.view",
  "warehouse-location-setup": "settings.location-settings.view",
  "integration-sync": "settings.integration.view",
  "company-settings": "settings.company.view",
  "user-management": "settings.user-management.view",
  "custom-fields": "settings.custom-fields.view",
  "fifo-demo": "settings.system-config.view", // Demo pages inherit from system config
  "permissions-demo": "settings.user-management.view",
  "help-system-demo": "settings.system-config.view",
};

/**
 * Hook to filter menu items based on user permissions
 * Returns only the menu items the current user has permission to view
 */
export function usePermissionFilteredMenu(menuItems: MenuItem[]): MenuItem[] {
  const { hasPermission, hasAnyPermission } = usePermissions();

  return useMemo(() => {
    const filterItems = (items: MenuItem[]): MenuItem[] => {
      return items
        .map((item) => {
          // Get required permissions for this item
          const requiredPermissions = MENU_PERMISSIONS[item.id];
          
          // If no permissions defined, show the item (backward compatibility)
          if (!requiredPermissions) {
            // Still filter children if they exist
            if (item.children && item.children.length > 0) {
              const filteredChildren = filterItems(item.children);
              // Only show parent if it has visible children
              if (filteredChildren.length > 0) {
                return { ...item, children: filteredChildren };
              }
              return null;
            }
            return item;
          }
          
          // Check permissions
          let hasAccess = false;
          if (Array.isArray(requiredPermissions)) {
            // For parent sections, show if user has ANY of the permissions
            hasAccess = hasAnyPermission(requiredPermissions);
          } else {
            // For individual items, check single permission
            hasAccess = hasPermission(requiredPermissions);
          }
          
          if (!hasAccess) {
            return null;
          }
          
          // If item has children, filter them recursively
          if (item.children && item.children.length > 0) {
            const filteredChildren = filterItems(item.children);
            // Only show parent if it has visible children
            if (filteredChildren.length > 0) {
              return { ...item, children: filteredChildren };
            }
            // If parent has permission but no visible children, still show it
            return item;
          }
          
          return item;
        })
        .filter((item): item is MenuItem => item !== null);
    };

    return filterItems(menuItems);
  }, [menuItems, hasPermission, hasAnyPermission]);
}
